const BIV_VARIANT_CHANGED_EVENT = "product:variant:changed";

class BivSoldOutAlternatives extends HTMLElement {
  constructor() {
    super();
    this.syncVersion = 0;
    this.recommendationsPromise = null;
  }

  connectedCallback() {
    this.controller = new AbortController();
    this.productSection = this.closest("[js-product-section]");
    this.options = this.#readJson("[data-biv-product-options]", []);
    this.variants = this.#readJson("[data-biv-product-variants]", []);
    this.fallbackProducts = this.#readJson("[data-biv-fallback-products]", []);

    if (!this.productSection) {
      this.hidden = true;
      return;
    }

    this.productSection.addEventListener(
      BIV_VARIANT_CHANGED_EVENT,
      () => requestAnimationFrame(() => this.#sync()),
      { signal: this.controller.signal },
    );

    this.productSection.addEventListener(
      "change",
      (event) => {
        if (event.target.matches?.("[js-variant-input]")) {
          requestAnimationFrame(() => this.#sync());
        }
      },
      { signal: this.controller.signal },
    );

    requestAnimationFrame(() => this.#sync());
  }

  disconnectedCallback() {
    this.controller?.abort();
  }

  #readJson(selector, fallback) {
    const node = this.querySelector(selector);
    if (!node) return fallback;

    try {
      return JSON.parse(node.textContent || "");
    } catch (_error) {
      return fallback;
    }
  }

  #currentState() {
    const variantIdInput = this.productSection.querySelector(
      'form[data-product-form] input[name="id"]',
    );
    const variantId = variantIdInput?.value;

    if (!variantId) return null;

    const variant = this.variants.find(
      (item) => String(item.id) === String(variantId),
    );
    if (!variant) return null;

    const sizeIndex = this.options.findIndex((option) =>
      String(option).toLowerCase().includes("size"),
    );
    if (sizeIndex < 0) return null;

    const size =
      variant.options?.[sizeIndex] ?? variant[`option${sizeIndex + 1}`] ?? null;

    if (!size) return null;

    return { variant, size: String(size).trim() };
  }

  async #sync() {
    const version = ++this.syncVersion;
    const state = this.#currentState();

    if (!state || state.variant.available !== false) {
      this.#hide();
      return;
    }

    const relatedProducts = await this.#loadRecommendations();
    if (version !== this.syncVersion) return;

    const latestState = this.#currentState();
    if (
      !latestState ||
      latestState.variant.available !== false ||
      String(latestState.variant.id) !== String(state.variant.id) ||
      this.#normalize(latestState.size) !== this.#normalize(state.size)
    ) {
      return;
    }

    let matches = this.#findMatches(relatedProducts, state.size);

    // Shopify related recommendations are probabilistic. When they return no
    // exact-size substitute, use the product's collection snapshot instead.
    if (matches.length === 0) {
      matches = this.#findMatches(this.fallbackProducts, state.size);
    }

    if (matches.length === 0) {
      this.#hide();
      return;
    }

    this.#render(matches, state.size);
  }

  #findMatches(products, requestedSize) {
    const matches = [];

    for (const product of Array.isArray(products) ? products : []) {
      if (String(product.id) === String(this.dataset.productId)) continue;

      const variant = (product.variants || []).find(
        (item) =>
          item.available === true &&
          this.#variantMatchesSize(product, item, requestedSize),
      );

      if (variant) {
        matches.push({ product, variant });
      }

      if (matches.length === 3) break;
    }

    return matches;
  }

  #loadRecommendations() {
    if (this.recommendationsPromise) return this.recommendationsPromise;

    const root = window.Shopify?.routes?.root || "/";
    const params = new URLSearchParams({
      product_id: this.dataset.productId,
      limit: "10",
      intent: "related",
    });

    this.recommendationsPromise = fetch(
      `${root}recommendations/products.json?${params.toString()}`,
      {
        credentials: "same-origin",
        headers: { Accept: "application/json" },
      },
    )
      .then((response) => (response.ok ? response.json() : { products: [] }))
      .then((payload) => (Array.isArray(payload.products) ? payload.products : []))
      .catch(() => []);

    return this.recommendationsPromise;
  }

  #variantMatchesSize(product, variant, requestedSize) {
    const productOptions = Array.isArray(product.options) ? product.options : [];
    const sizeIndex = productOptions.findIndex((option) => {
      const optionName =
        typeof option === "string" ? option : option?.name || option?.title || "";
      return String(optionName).toLowerCase().includes("size");
    });

    if (sizeIndex >= 0) {
      const value =
        variant.options?.[sizeIndex] ??
        variant[`option${sizeIndex + 1}`] ??
        null;
      return this.#normalize(value) === this.#normalize(requestedSize);
    }

    return (
      Array.isArray(variant.options) &&
      variant.options.some(
        (value) => this.#normalize(value) === this.#normalize(requestedSize),
      )
    );
  }

  #normalize(value) {
    return String(value ?? "").trim().toLowerCase();
  }

  #render(matches, size) {
    const list = this.querySelector("[data-biv-sold-out-list]");
    const sizeLabel = this.querySelector("[data-biv-sold-out-size]");
    const eyebrow = this.querySelector(".biv-sold-out-alternatives__eyebrow");
    if (!list || !sizeLabel) return;

    if (eyebrow) eyebrow.textContent = "YOUR SIZE IS SOLD OUT";
    sizeLabel.textContent = size;
    list.replaceChildren();

    for (const { product, variant } of matches) {
      list.append(this.#buildCard(product, variant, size));
    }

    this.hidden = false;
  }

  #buildCard(product, variant, size) {
    const link = document.createElement("a");
    link.className = "biv-sold-out-alternatives__card";
    link.href = this.#variantUrl(product.url, variant.id);
    link.setAttribute("aria-label", `${product.title}, ${size} available`);

    const imageUrl = this.#imageUrl(product);
    if (imageUrl) {
      const image = document.createElement("img");
      image.className = "biv-sold-out-alternatives__image";
      image.src = imageUrl;
      image.alt = product.title || "";
      image.loading = "lazy";
      image.width = 160;
      image.height = 160;
      link.append(image);
    }

    const content = document.createElement("span");
    content.className = "biv-sold-out-alternatives__card-content";

    const title = document.createElement("span");
    title.className = "biv-sold-out-alternatives__title";
    title.textContent = product.title || "";

    const price = document.createElement("span");
    price.className = "biv-sold-out-alternatives__price";
    price.textContent = this.#formatMoney(variant.price ?? product.price);

    const availability = document.createElement("span");
    availability.className = "biv-sold-out-alternatives__availability";
    availability.textContent = `${size} AVAILABLE`;

    content.append(title, price, availability);
    link.append(content);

    return link;
  }

  #variantUrl(productUrl, variantId) {
    try {
      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set("variant", variantId);
      return url.toString();
    } catch (_error) {
      return `${productUrl}${String(productUrl).includes("?") ? "&" : "?"}variant=${variantId}`;
    }
  }

  #imageUrl(product) {
    const featured = product.featured_image;
    if (typeof featured === "string") return featured;
    if (featured?.src) return featured.src;

    const firstImage = product.images?.[0];
    if (typeof firstImage === "string") return firstImage;
    return firstImage?.src || "";
  }

  #formatMoney(value) {
    const cents = Number(value);
    if (!Number.isFinite(cents)) return "";

    const currency = window.Shopify?.currency?.active || "ILS";
    const locale = document.documentElement.lang || "en";

    try {
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
      }).format(cents / 100);
    } catch (_error) {
      return `${(cents / 100).toFixed(2)} ${currency}`;
    }
  }

  #hide() {
    this.hidden = true;
    const list = this.querySelector("[data-biv-sold-out-list]");
    list?.replaceChildren();
  }
}

if (!customElements.get("biv-sold-out-alternatives")) {
  customElements.define("biv-sold-out-alternatives", BivSoldOutAlternatives);
}
